/**
 * Moeda por idioma do site: cada locale exibe preços na moeda da região
 * padrão daquele idioma (mesmas regiões dos OG locales em `pages/index.tsx`).
 * Os valores da landing são ilustrativos — a conversão usa taxas fixas
 * aproximadas e arredonda para números "de vitrine", não é cotação em tempo
 * real. A cobrança de verdade acontece no app.
 */

type LocaleCurrency = {
  /** Código ISO 4217 exibido e usado no JSON-LD. */
  currency: string
  /** BCP 47 completo para o Intl.NumberFormat (símbolo + separadores). */
  formatLocale: string
  /** Taxa fixa aproximada BRL → moeda, só para os valores ilustrativos. */
  rateFromBrl: number
}

const LOCALE_CURRENCIES: Record<string, LocaleCurrency> = {
  pt: { currency: 'BRL', formatLocale: 'pt-BR', rateFromBrl: 1 },
  en: { currency: 'USD', formatLocale: 'en-US', rateFromBrl: 0.19 },
  es: { currency: 'EUR', formatLocale: 'es-ES', rateFromBrl: 0.16 },
  fr: { currency: 'EUR', formatLocale: 'fr-FR', rateFromBrl: 0.16 },
  de: { currency: 'EUR', formatLocale: 'de-DE', rateFromBrl: 0.16 },
  it: { currency: 'EUR', formatLocale: 'it-IT', rateFromBrl: 0.16 },
  ja: { currency: 'JPY', formatLocale: 'ja-JP', rateFromBrl: 27 },
  ko: { currency: 'KRW', formatLocale: 'ko-KR', rateFromBrl: 250 },
  ar: { currency: 'AED', formatLocale: 'ar-AE', rateFromBrl: 0.68 },
  he: { currency: 'ILS', formatLocale: 'he-IL', rateFromBrl: 0.62 },
  zh: { currency: 'CNY', formatLocale: 'zh-CN', rateFromBrl: 1.3 },
}

const FALLBACK = LOCALE_CURRENCIES.pt

/** Degraus "de vitrine" para o dígito líder (25, 250, 3 000, 30 000…). */
const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10]

function niceRound(value: number): number {
  if (value <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = NICE_STEPS.reduce((best, candidate) =>
    Math.abs(candidate - normalized) < Math.abs(best - normalized)
      ? candidate
      : best,
  )
  return step * magnitude
}

/** Moeda ISO 4217 do locale (ex.: `ja` → `JPY`), com fallback em BRL. */
export function localeCurrency(locale?: string): string {
  return (LOCALE_CURRENCIES[locale ?? ''] ?? FALLBACK).currency
}

/**
 * Converte um valor ilustrativo em BRL para a moeda do locale e formata com
 * símbolo local (ex.: 120 → `R$ 120`, `$25`, `¥3,000`). Em BRL o valor passa
 * sem arredondar; nas demais moedas arredonda para um número redondo.
 */
export function formatLocalPrice(amountBrl: number, locale?: string): string {
  const { currency, formatLocale, rateFromBrl } =
    LOCALE_CURRENCIES[locale ?? ''] ?? FALLBACK
  const amount =
    currency === 'BRL' ? amountBrl : niceRound(amountBrl * rateFromBrl)

  return new Intl.NumberFormat(formatLocale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}
